import type { ReactNode } from "react";
import { useId } from "react";
import { cn } from "~/utils/cn";
import { Switch } from "./switch";

interface ToggleCardProps {
	icon: ReactNode;
	title: string;
	description: string;
	checked: boolean;
	onCheckedChange: (checked: boolean) => void;
	disabled?: boolean;
	/** Replaces `description` while disabled, to explain why. */
	disabledHint?: string;
	className?: string;
}

/**
 * A bordered row pairing a labelled description with a `Switch`.
 *
 * The text block is a `<label>` for the switch — which sits outside it — so the
 * title and description are a click target without nesting one interactive
 * element inside another.
 */
export const ToggleCard = ({
	icon,
	title,
	description,
	checked,
	onCheckedChange,
	disabled = false,
	disabledHint,
	className,
}: ToggleCardProps) => {
	const switchId = useId();
	const descriptionId = useId();

	return (
		<div
			className={cn(
				"flex items-center gap-3 rounded-lg border px-3.5 py-3 transition-colors",
				disabled && "border-border/40 bg-muted/10 opacity-70",
				!disabled && checked && "border-primary/30 bg-primary/5",
				!disabled &&
					!checked &&
					"border-border/60 bg-muted/20 hover:border-border",
				className
			)}
		>
			<span
				className={cn(
					"flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors",
					checked ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
				)}
			>
				{icon}
			</span>
			<label
				className={cn(
					"flex flex-1 flex-col gap-0.5",
					disabled ? "cursor-not-allowed" : "cursor-pointer"
				)}
				htmlFor={switchId}
			>
				<span
					className={cn(
						"font-medium text-xs leading-none",
						checked ? "text-foreground" : "text-muted-foreground"
					)}
				>
					{title}
				</span>
				<span
					className="text-[11px] text-muted-foreground leading-snug"
					id={descriptionId}
				>
					{disabled ? (disabledHint ?? description) : description}
				</span>
			</label>
			<Switch
				aria-describedby={descriptionId}
				checked={checked}
				disabled={disabled}
				id={switchId}
				onCheckedChange={onCheckedChange}
			/>
		</div>
	);
};
